/**
 * <SamFloatingTile> — Sam sur l'écran d'une activité, feuille fermée (organism).
 *
 * Sa tuile, et à sa gauche l'aperçu de ce qu'il vient de dire quand il a quelque chose à dire.
 * Trois états, tous en props :
 *  - fermée : la tuile seule ;
 *  - Sam vient de parler : l'aperçu (`peek`) et le point sur la tuile ;
 *  - l'aperçu est parti, l'élève n'a pas ouvert : le point seul (`unread`).
 *
 * L'écran décide du reste, comme aujourd'hui : quand l'aperçu arrive (son bonjour à 5 s, sa
 * relance à 3 min, une réponse reçue feuille fermée), combien de temps il tient, et que rien ne
 * s'affiche pendant que l'élève regarde ailleurs. La tuile disparaît quand la feuille s'ouvre et
 * quand la copie est rendue : c'est l'écran qui ne la monte plus.
 *
 * Elle ne se place pas elle-même : l'écran d'activité (`<StudentActivityPage samTile>`) la pose en
 * bas à droite, au-dessus de la barre de copie (au bas de la colonne de copie sur un grand écran).
 * Rien n'y bouge (le halo et le rebond de l'ancienne tuile ne sont pas repris ici).
 *
 * Composes DS pieces only — `<SamTile>`, `<SamPeek>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { SamPeek } from "../../molecules/SamPeek"
import { SamTile } from "../../molecules/SamTile"
import { samPeekText } from "../../utils/samPeek"

export type SamFloatingTileProps = {
    /** La tuile ou l'aperçu touchés : l'écran ouvre la feuille. */
    onOpen: () => void
    /** Ce que Sam vient de dire. Absent ou vide : la tuile seule. Coupé ici à 80 caractères. */
    peek?: string | null
    /** La croix de l'aperçu. */
    onDismissPeek?: () => void
    /** Sam a parlé en dernier et l'élève n'a pas ouvert. Défaut : vrai tant qu'un aperçu est là. */
    unread?: boolean
    testID?: string
}

export function SamFloatingTile(props: SamFloatingTileProps) {
    const { onOpen, peek, onDismissPeek, unread, testID } = props
    const peekText = samPeekText(peek)

    return (
        // `box-none` : entre l'aperçu et la tuile, l'activité derrière reste touchable.
        <View style={styles.root} pointerEvents="box-none" testID={testID}>
            {peekText !== "" && (
                <View style={styles.peek}>
                    <SamPeek text={peekText} onPress={onOpen} onDismiss={onDismissPeek} />
                </View>
            )}
            <SamTile onPress={onOpen} unread={unread ?? peekText !== ""} />
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: {
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "flex-end",
        gap: theme.spacing["2"],
    },
    /** L'aperçu se pose un peu au-dessus du pied de la tuile : sa queue vise la tête, pas le nœud. */
    peek: {
        flexShrink: 1,
        marginBottom: theme.spacing["3"],
    },
}))
